import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import { useBranding } from '../context/BrandingContext';
import axios from 'axios';
import CONFIG from '../config';
import { getToken } from '../utils/auth';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/common/SEO';

/* ── Default payment methods (overridden by branding.checkout config) ── */
const DEFAULT_METHODS = {
    cash_on_delivery: { enabled: true, label: 'Cash on Delivery', icon: 'fa-money-bill-wave', color: 'emerald', instructions: 'Pay when your order arrives at your door.' },
    bank_transfer: { enabled: true, label: 'Bank Transfer', icon: 'fa-university', color: 'blue', accountNumber: '', bankName: '', instructions: 'Transfer the total to our bank account and send the receipt to our WhatsApp.' },
    vodafone_cash: { enabled: false, label: 'Vodafone / Orange Cash', icon: 'fa-mobile-alt', color: 'red', accountNumber: '', instructions: 'Send the amount to our Vodafone Cash number and send the receipt.' },
    instapay: { enabled: false, label: 'InstaPay', icon: 'fa-bolt', color: 'purple', accountNumber: '', instructions: 'Transfer via InstaPay to our registered account.' },
    fawry: { enabled: false, label: 'Fawry', icon: 'fa-store', color: 'orange', accountNumber: '', instructions: 'Pay at any Fawry outlet using our reference number.' },
    paypal: { enabled: false, label: 'PayPal', icon: 'fa-paypal', color: 'indigo', instructions: 'You will be redirected to PayPal to complete payment.' },
    stripe_card: { enabled: false, label: 'Credit / Debit Card', icon: 'fa-credit-card', color: 'gray', instructions: 'Secure card payment via Stripe.' },
};

const COLOR_MAP = {
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    blue: 'text-blue-600 bg-blue-50 border-blue-200',
    red: 'text-red-600 bg-red-50 border-red-200',
    purple: 'text-purple-600 bg-purple-50 border-purple-200',
    orange: 'text-orange-600 bg-orange-50 border-orange-200',
    indigo: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    gray: 'text-gray-600 bg-gray-50 border-gray-200',
};

/* ── Stripe card form component (vanilla, no library needed for UI) ── */
const StripeCardForm = ({ cardData, onChange }) => (
    <div className="grid grid-cols-1 gap-4 mt-4">
        <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Card Number</label>
            <input maxLength={19} placeholder="1234 5678 9012 3456"
                value={cardData.number} onChange={e => onChange({ ...cardData, number: e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim() })}
                className="w-full bg-gray-50 border-none p-4 rounded-xl font-mono text-sm focus:ring-2 focus:ring-brand-primary/20 focus:bg-white transition-all" />
        </div>
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Expiry</label>
                <input maxLength={5} placeholder="MM/YY"
                    value={cardData.expiry} onChange={e => onChange({ ...cardData, expiry: e.target.value.replace(/\D/g, '').replace(/^(.{2})/, '$1/').slice(0, 5) })}
                    className="w-full bg-gray-50 border-none p-4 rounded-xl font-mono text-sm focus:ring-2 focus:ring-brand-primary/20 focus:bg-white transition-all" />
            </div>
            <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">CVV</label>
                <input maxLength={4} placeholder="•••"
                    value={cardData.cvv} onChange={e => onChange({ ...cardData, cvv: e.target.value.replace(/\D/g, '') })}
                    className="w-full bg-gray-50 border-none p-4 rounded-xl font-mono text-sm focus:ring-2 focus:ring-brand-primary/20 focus:bg-white transition-all" />
            </div>
        </div>
        <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Name on Card</label>
            <input placeholder="John Doe"
                value={cardData.name} onChange={e => onChange({ ...cardData, name: e.target.value })}
                className="w-full bg-gray-50 border-none p-4 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary/20 focus:bg-white transition-all" />
        </div>
        <p className="text-[10px] text-gray-400 flex items-center gap-1.5">
            <i className="fas fa-lock text-green-500" /> Secured by Stripe. Your card details are encrypted.
        </p>
    </div>
);

/* ── PayPal Button (loads SDK dynamically) ── */
const PayPalButton = ({ amount, currency, onSuccess, onError }) => {
    const [ready, setReady] = useState(false);
    const [paypalClientId, setPaypalClientId] = useState(null);

    useEffect(() => {
        // Fetch publishable PayPal client ID from our backend
        axios.get(`${CONFIG.API_URL}/api/payment/config`, {
            headers: { Authorization: `Bearer ${getToken()}` }
        }).then(res => {
            if (res.data.paypal && res.data.paypalClientId) {
                setPaypalClientId(res.data.paypalClientId);
            }
        }).catch(() => { });
    }, []);

    useEffect(() => {
        if (!paypalClientId) return;
        if (window.paypal) { setReady(true); return; }
        const script = document.createElement('script');
        script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&currency=${currency}`;
        script.onload = () => setReady(true);
        document.body.appendChild(script);
        return () => { try { document.body.removeChild(script); } catch (e) { } };
    }, [paypalClientId, currency]);

    useEffect(() => {
        if (!ready || !window.paypal) return;
        const container = document.getElementById('paypal-button-container');
        if (!container) return;
        container.innerHTML = '';
        window.paypal.Buttons({
            createOrder: async () => {
                const res = await axios.post(`${CONFIG.API_URL}/api/payment/paypal/create-order`,
                    { amount, currency }, { headers: { Authorization: `Bearer ${getToken()}` } });
                return res.data.id;
            },
            onApprove: async (data) => {
                const res = await axios.post(`${CONFIG.API_URL}/api/payment/paypal/capture-order`,
                    { orderID: data.orderID }, { headers: { Authorization: `Bearer ${getToken()}` } });
                if (res.data.status === 'COMPLETED') onSuccess('paid');
                else onError('PayPal payment not completed');
            },
            onError: (err) => onError(err.message || 'PayPal error'),
        }).render('#paypal-button-container');
    }, [ready, amount, currency, onSuccess, onError]);

    if (!paypalClientId) return (
        <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-xl">PayPal is not configured for this store.</p>
    );
    if (!ready) return <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />;
    return <div id="paypal-button-container" className="mt-4" />;
};

/* ── Main Checkout Component ── */
const Checkout = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language.startsWith('ar');
    const { cartItems, clearCart } = useCart();
    const { formatPrice, currency } = useCurrency();
    const { branding } = useBranding();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [formData, setFormData] = useState({ shippingAddress: '', contactPhone: '', notes: '' });
    const [selectedMethod, setSelectedMethod] = useState('');
    const [cardData, setCardData] = useState({ number: '', expiry: '', cvv: '', name: '' });
    const [stripeProcessing, setStripeProcessing] = useState(false);

    // Merge branding payment config with defaults
    const brandingMethods = branding?.checkout?.paymentMethods || {};
    const paymentMethods = Object.entries(DEFAULT_METHODS).map(([id, defaults]) => {
        const override = brandingMethods[id] || {};
        return { id, ...defaults, ...override };
    }).filter(m => m.enabled);

    const receiptWhatsApp = branding?.checkout?.receiptWhatsApp || branding?.socialLinks?.whatsapp || '';

    const priceField = currency === 'SDG' ? 'price_sdg' : (currency === 'EGP' ? 'price_egp' : 'price');
    const total = cartItems.reduce((acc, item) => acc + (item[priceField] || item.price || 0) * item.quantity, 0);

    const placeOrder = useCallback(async (paymentStatus = 'pending') => {
        setLoading(true);
        try {
            const token = getToken();
            const res = await axios.post(`${CONFIG.API_URL}/api/orders/checkout`,
                { ...formData, paymentMethod: selectedMethod, currency, paymentStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOrderId(res.data.orderId);
            clearCart();
            setStep(3);
        } catch (err) {
            alert(err.response?.data?.message || t('checkout.failed', 'Checkout failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    }, [formData, selectedMethod, currency, clearCart, t]);

    const handleManualCheckout = () => {
        if (!formData.shippingAddress || !formData.contactPhone) {
            alert(t('checkout.all_fields', 'Please fill all required fields'));
            return;
        }
        if (!selectedMethod) {
            alert('Please select a payment method');
            return;
        }
        placeOrder('pending');
    };

    const handleStripeSubmit = async () => {
        if (!cardData.number || !cardData.expiry || !cardData.cvv || !cardData.name) {
            alert('Please fill all card details'); return;
        }
        setStripeProcessing(true);
        try {
            await axios.post(`${CONFIG.API_URL}/api/payment/stripe/create-intent`,
                { amount: total, currency },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            // In production with stripe-js, we'd confirm with clientSecret.
            // Here we place the order and mark for manual processing.
            await placeOrder('processing');
        } catch (err) {
            alert('Card payment failed. Please try again or choose another method.');
        } finally {
            setStripeProcessing(false);
        }
    };

    // unused selectedMethodObj variable. it should be removed. please confirm it and remove it!
    // const selectedMethodObj = paymentMethods.find(m => m.id === selectedMethod);

    if (cartItems.length === 0 && step < 3) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center text-3xl text-gray-200 mb-4">
                    <i className="fas fa-shopping-cart" />
                </div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">Your cart is empty</h2>
                <button onClick={() => navigate('/shop')} className="mt-6 bg-brand-primary text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest">
                    Browse Shop
                </button>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
            </Helmet>
            <SEO />
            <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
                {/* Step Indicator */}
                {step < 3 && (
                    <div className="flex items-center justify-center mb-12">
                        {['Shipping', 'Payment', 'Done'].map((label, i) => {
                            const s = i + 1;
                            return (
                                <React.Fragment key={s}>
                                    <div className="flex flex-col items-center gap-1">
                                        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-sm transition-all duration-500 ${step >= s ? 'bg-brand-primary text-white shadow-lg shadow-brand-primary/20' : 'bg-gray-100 text-gray-300'}`}>
                                            {step > s ? <i className="fas fa-check text-xs" /> : s}
                                        </div>
                                        <span className={`text-[9px] font-black uppercase tracking-widest ${step >= s ? 'text-brand-primary' : 'text-gray-300'}`}>{label}</span>
                                    </div>
                                    {s < 3 && <div className={`w-16 lg:w-24 h-1 mx-3 mb-4 rounded-full transition-all duration-500 ${step > s ? 'bg-brand-primary' : 'bg-gray-100'}`} />}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                <div className={`grid gap-8 ${step < 3 ? 'lg:grid-cols-[1fr_380px]' : ''}`}>
                    {/* ── Left Column: Step Content ── */}
                    <div>
                        {/* ── STEP 1: Shipping ── */}
                        {step === 1 && (
                            <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 space-y-6">
                                <h2 className="text-2xl font-black text-gray-900">{t('checkout.shipping_info', 'Shipping Information')}</h2>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('checkout.address', 'Shipping Address')} *</label>
                                    <textarea rows={3}
                                        className="w-full bg-gray-50 border-none p-4 rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium text-sm resize-none"
                                        placeholder="Street, city, state, postal code..."
                                        value={formData.shippingAddress}
                                        onChange={e => setFormData({ ...formData, shippingAddress: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('checkout.phone', 'Contact Phone')} *</label>
                                    <input type="tel"
                                        className="w-full bg-gray-50 border-none p-4 rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium text-sm"
                                        placeholder="+1 234 567 8900"
                                        value={formData.contactPhone}
                                        onChange={e => setFormData({ ...formData, contactPhone: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{t('checkout.notes', 'Order Notes')} <span className="text-gray-300">(Optional)</span></label>
                                    <input
                                        className="w-full bg-gray-50 border-none p-4 rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium text-sm"
                                        placeholder="Any special instructions?"
                                        value={formData.notes}
                                        onChange={e => setFormData({ ...formData, notes: e.target.value })} />
                                </div>
                                <div className="pt-4 border-t border-gray-50 flex justify-end">
                                    <button
                                        onClick={() => {
                                            if (!formData.shippingAddress || !formData.contactPhone) { alert('Please fill address and phone'); return; }
                                            setStep(2);
                                        }}
                                        className="bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-brand-primary transition-all shadow-xl">
                                        Continue to Payment →
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── STEP 2: Payment ── */}
                        {step === 2 && (
                            <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 space-y-5">
                                <h2 className="text-2xl font-black text-gray-900">{t('checkout.payment_method', 'Payment Method')}</h2>

                                {paymentMethods.length === 0 && (
                                    <p className="text-gray-400 text-sm">No payment methods configured for this store.</p>
                                )}

                                <div className="space-y-3">
                                    {paymentMethods.map(method => {
                                        const isSelected = selectedMethod === method.id;
                                        const colorClass = COLOR_MAP[method.color] || COLOR_MAP.gray;
                                        return (
                                            <div key={method.id}>
                                                <button
                                                    onClick={() => setSelectedMethod(method.id)}
                                                    className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 transition-all text-left ${isSelected ? 'border-brand-primary bg-brand-primary/5 shadow-md' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                                                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg border ${isSelected ? 'bg-brand-primary text-white border-brand-primary' : colorClass}`}>
                                                        <i className={`fas ${method.icon}`} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className={`font-black text-sm ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>{method.label}</p>
                                                        {method.accountNumber && (
                                                            <p className="text-[10px] text-gray-400 font-bold mt-0.5">Account: {method.accountNumber}</p>
                                                        )}
                                                    </div>
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'border-brand-primary bg-brand-primary' : 'border-gray-200'}`}>
                                                        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                                    </div>
                                                </button>

                                                {/* Expanded instructions */}
                                                {isSelected && (
                                                    <div className={`mt-2 ml-4 p-4 rounded-2xl border ${colorClass} bg-opacity-50`}>
                                                        {method.instructions && (
                                                            <p className="text-sm leading-relaxed mb-3">{method.instructions}</p>
                                                        )}
                                                        {method.bankName && (
                                                            <p className="text-xs font-bold">Bank: {method.bankName}</p>
                                                        )}
                                                        {receiptWhatsApp && ['bank_transfer', 'vodafone_cash', 'instapay', 'fawry'].includes(method.id) && (
                                                            <a href={`https://wa.me/${receiptWhatsApp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 mt-3 bg-green-500 text-white px-4 py-2 rounded-xl text-xs font-black">
                                                                <i className="fab fa-whatsapp" /> Send Receipt on WhatsApp
                                                            </a>
                                                        )}

                                                        {/* Stripe card form */}
                                                        {method.id === 'stripe_card' && (
                                                            <StripeCardForm cardData={cardData} onChange={setCardData} />
                                                        )}

                                                        {/* PayPal button */}
                                                        {method.id === 'paypal' && (
                                                            <PayPalButton
                                                                amount={total.toFixed(2)}
                                                                currency={currency}
                                                                onSuccess={(status) => placeOrder(status)}
                                                                onError={(msg) => alert(msg)}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Actions */}
                                <div className="pt-6 border-t border-gray-50 flex items-center justify-between">
                                    <button onClick={() => setStep(1)} className="text-gray-400 font-black text-xs uppercase tracking-widest hover:text-gray-700 transition-all">
                                        ← Back
                                    </button>
                                    <div className="flex items-center gap-4">
                                        <div className="text-right hidden sm:block">
                                            <p className="text-[9px] text-gray-300 font-black uppercase tracking-widest">Total</p>
                                            <p className="text-lg font-black text-gray-900">{formatPrice({ price: total })}</p>
                                        </div>
                                        {/* Only show manual place order for non-paypal methods */}
                                        {selectedMethod !== 'paypal' && (
                                            <button
                                                onClick={selectedMethod === 'stripe_card' ? handleStripeSubmit : handleManualCheckout}
                                                disabled={loading || stripeProcessing || !selectedMethod}
                                                className="bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-brand-primary transition-all disabled:opacity-40">
                                                {(loading || stripeProcessing) ? (
                                                    <span className="flex items-center gap-2"><i className="fas fa-circle-notch fa-spin" /> Processing...</span>
                                                ) : 'Place Order'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── STEP 3: Success ── */}
                        {step === 3 && (
                            <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl p-12 text-center space-y-8 max-w-lg mx-auto">
                                <div className="w-24 h-24 mx-auto bg-green-500 text-white rounded-full flex items-center justify-center text-4xl shadow-2xl shadow-green-200 animate-bounce-in">
                                    <i className="fas fa-check" />
                                </div>
                                <div>
                                    <h2 className="text-4xl font-black text-gray-900 tracking-tight mb-3">Order Received!</h2>
                                    {orderId && (
                                        <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-xl mb-4">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Order ID</span>
                                            <span className="text-xs font-black text-gray-700 font-mono">{orderId}</span>
                                        </div>
                                    )}
                                    <p className="text-gray-500 leading-relaxed">
                                        Thank you! Your order has been placed. Our team will contact you shortly for confirmation.
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <button onClick={() => navigate('/profile/orders')}
                                        className="bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-brand-primary transition-all">
                                        View My Orders
                                    </button>
                                    <button onClick={() => navigate('/')}
                                        className="bg-gray-50 text-gray-500 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all">
                                        Back to Home
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Right Column: Order Summary ── */}
                    {step < 3 && (
                        <div className="space-y-4">
                            <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-6 sticky top-24">
                                <h3 className="font-black text-gray-900 text-sm uppercase tracking-widest mb-5">Order Summary</h3>
                                <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
                                    {cartItems.map(item => (
                                        <div key={item.id} className="flex items-center gap-3">
                                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
                                                <img src={item.imageUrl || '/placeholder-furniture.jpg'} alt={item.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-xs text-gray-900 line-clamp-1">{item.name}</p>
                                                <p className="text-[10px] text-gray-400 font-bold">Qty: {item.quantity}</p>
                                            </div>
                                            <span className="text-xs font-black text-gray-900">{formatPrice({ price: (item[priceField] || item.price) * item.quantity })}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-5 pt-5 border-t border-gray-50 space-y-2">
                                    <div className="flex justify-between text-xs text-gray-400 font-bold">
                                        <span>Subtotal</span><span>{formatPrice({ price: total })}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-gray-400 font-bold">
                                        <span>Shipping</span><span className="text-green-500">Free</span>
                                    </div>
                                    <div className="flex justify-between text-base font-black text-gray-900 pt-2 border-t border-gray-50">
                                        <span>Total</span><span>{formatPrice({ price: total })}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 text-[10px] font-bold text-gray-300 uppercase tracking-widest">
                                <i className="fas fa-lock text-green-400" />
                                <span>Secure Checkout</span>
                                <i className="fas fa-shield-alt text-blue-400" />
                                <span>Encrypted</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Checkout;
